import * as ContextMenu from "@radix-ui/react-context-menu";
import {
  IconCopy,
  IconFolderPlus,
  IconRefresh,
  IconSquareCheck,
  IconSquareOff,
  IconTrash,
  IconTransfer,
  IconUpload,
} from "@tabler/icons-react";

import { useFileManager } from "@/components/core/FileManager";
import { useFileManagerUI } from "@/components/core/FileManager";

import styles from "./FileManagerContextMenu.module.scss";

export function FileManagerBodySelectionItems() {
  const { items } = useFileManager();
  const { hasSelection, selectedCount, selectAll, deselectAll } = useFileManagerUI();

  const allSelected = selectedCount === items.length && items.length > 0;

  if (items.length === 0) return null;

  return (
    <>
      {!allSelected && (
        <ContextMenu.Item className={styles.item} onSelect={() => selectAll(items)}>
          <IconSquareCheck size={16} />
          Select all
        </ContextMenu.Item>
      )}
      {hasSelection && (
        <ContextMenu.Item className={styles.item} onSelect={deselectAll}>
          <IconSquareOff size={16} />
          Deselect all ({selectedCount})
        </ContextMenu.Item>
      )}
    </>
  );
}

export function FileManagerBodyOrganizeItems() {
  const { permissions } = useFileManager();
  const { hasSelection, selectedCount, selectedItems, openMove, openDuplicate, openDelete } =
    useFileManagerUI();
  const { canEdit, canDelete } = permissions;

  if (!hasSelection) return null;

  function handleMoveSelected() {
    if (selectedItems.length === 0) return;
    openMove({ type: selectedItems[0].type, id: selectedItems[0].id, name: selectedItems[0].name });
  }

  function handleDuplicateSelected() {
    if (selectedItems.length === 0) return;
    openDuplicate({
      type: selectedItems[0].type,
      id: selectedItems[0].id,
      name: selectedItems[0].name,
    });
  }

  function handleDeleteSelected() {
    if (selectedItems.length === 0) return;
    openDelete({
      items: selectedItems.map((selectedItem) => ({
        type: selectedItem.type,
        id: selectedItem.id,
        name: selectedItem.name,
        s3Key: selectedItem.s3Key,
      })),
    });
  }

  return (
    <>
      {canEdit && (
        <ContextMenu.Item className={styles.item} onSelect={handleMoveSelected}>
          <IconTransfer size={16} />
          Move to...
        </ContextMenu.Item>
      )}
      {canEdit && (
        <ContextMenu.Item className={styles.item} onSelect={handleDuplicateSelected}>
          <IconCopy size={16} />
          Copy to...
        </ContextMenu.Item>
      )}
      {canDelete && (
        <ContextMenu.Item className={styles.destructiveItem} onSelect={handleDeleteSelected}>
          <IconTrash size={16} />
          Delete {selectedCount} {selectedCount === 1 ? "item" : "items"}
        </ContextMenu.Item>
      )}
    </>
  );
}

export function FileManagerBodyDeleteItems() {
  const { permissions } = useFileManager();
  const { hasSelection, selectedCount, selectedItems, openDelete } = useFileManagerUI();
  const { canDelete } = permissions;

  if (!hasSelection) return null;
  if (!canDelete) return null;
  if (selectedCount === 0) return null;

  function handleDeleteSelected() {
    openDelete({
      items: selectedItems.map((selectedItem) => ({
        type: selectedItem.type,
        id: selectedItem.id,
        name: selectedItem.name,
        s3Key: selectedItem.s3Key,
      })),
    });
  }

  return (
    <ContextMenu.Item className={styles.destructiveItem} onSelect={handleDeleteSelected}>
      <IconTrash size={16} />
      Delete {selectedCount} {selectedCount === 1 ? "item" : "items"}
    </ContextMenu.Item>
  );
}

export function FileManagerBodyCreateItems({ targetFolderId }: { targetFolderId?: string | null }) {
  const { permissions, currentFolderId, isLocked } = useFileManager();
  const { openCreateFolder, openUpload } = useFileManagerUI();

  const effectiveFolderId = targetFolderId !== undefined ? targetFolderId : currentFolderId;

  if (!permissions.canCreate || isLocked(effectiveFolderId, "add")) return null;

  return (
    <>
      <ContextMenu.Item
        className={styles.item}
        onSelect={() => openCreateFolder(effectiveFolderId)}
      >
        <IconFolderPlus size={16} />
        New folder
      </ContextMenu.Item>
      <ContextMenu.Item className={styles.item} onSelect={openUpload}>
        <IconUpload size={16} />
        Upload
      </ContextMenu.Item>
    </>
  );
}

export function FileManagerBodyRefreshItem() {
  const { refetch } = useFileManager();

  return (
    <ContextMenu.Item className={styles.item} onSelect={refetch}>
      <IconRefresh size={16} />
      Refresh
    </ContextMenu.Item>
  );
}

export function FileManagerBodyDefaultMenuItems() {
  const { items } = useFileManager();
  const { hasSelection } = useFileManagerUI();
  const { permissions } = useFileManager();

  const hasItems = items.length > 0;

  return (
    <>
      {hasItems && (
        <>
          <FileManagerBodySelectionItems />
          <ContextMenu.Separator className={styles.separator} />
        </>
      )}

      {hasSelection && (
        <>
          <FileManagerBodyOrganizeItems />
          <ContextMenu.Separator className={styles.separator} />
        </>
      )}

      {permissions.canCreate && (
        <>
          <FileManagerBodyCreateItems />
          <ContextMenu.Separator className={styles.separator} />
        </>
      )}

      <FileManagerBodyRefreshItem />
    </>
  );
}
